import { ReactNode, useCallback, useEffect, useState } from 'react';

import { cx } from '@libs/classnames';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { Table, TableCell, TableRow } from '@ui/components/Table';

import {
  GameMovementPlayer,
  GameMovementRow,
  ParticipantGameMovementRow,
  TeamGameMovementRow,
} from '@features/shared/api/typings';
import { ParticipantsRoundMovementRow } from '@features/tournament/ui/components/RoundMovement/components/ParticipantsRoundMovementRow';
import { TeamRoundMovementRow } from '@features/tournament/ui/components/RoundMovement/components/TeamRoundMovementRow';
import { TotalMovementRow } from '@features/tournament/ui/components/RoundMovement/components/TotalMovementRow';

import styles from './RoundMovement.module.css';

export function RoundMovement({
  className,
  rows,
  total,
  roundNumber,
  is2x2Mode,
  onGameClick,
  renderQuickPeekShield,
}: {
  className?: string;
  rows: GameMovementRow[];
  total?: {
    player1: GameMovementPlayer;
    player2: GameMovementPlayer;
  };
  roundNumber?: number;
  is2x2Mode?: boolean;
  onGameClick?: (gameId: string) => void;
  renderQuickPeekShield?: (userId: string) => ReactNode;
}) {
  const [visibleTeamIds, setVisibleTeamIds] = useState(new Set());

  const firstRow = rows && rows[0];
  const isTeam = firstRow && 'team1' in firstRow;

  const handleToggleTeam = useCallback(
    (teamId: string) => {
      const newTeamIds = new Set(visibleTeamIds);
      newTeamIds.has(teamId) ? newTeamIds.delete(teamId) : newTeamIds.add(teamId);
      setVisibleTeamIds(newTeamIds);
    },
    [visibleTeamIds, setVisibleTeamIds],
  );

  // При смене раунда очищаем, чтобы не прыгало
  useEffect(() => {
    setVisibleTeamIds(new Set());
  }, [roundNumber]);

  const hasTableNumbers = !isTeam || firstRow.tableNumber;

  return (
    <HorizontalScroll>
      <Table className={cx(styles.root, className)}>
        <TableRow header>
          {hasTableNumbers && <TableCell className={styles.tableHeader}>Стол</TableCell>}
          <TableCell>Игрок</TableCell>
          <TableCell className={styles.resultsHeader}>Результат</TableCell>
          <TableCell left colSpan={/*2*/ 1}>
            Игрок
          </TableCell>
        </TableRow>

        {rows.map((r) => {
          let row;
          if ('team1' in r) {
            row = r as TeamGameMovementRow;

            return (
              <TeamRoundMovementRow
                key={row.ordinalNumber + row.team1.teamId + row.team2.teamId}
                row={row}
                isToggled={visibleTeamIds.has(row.team1.teamId)}
                toggleTeam={handleToggleTeam}
                onGameClick={onGameClick}
                is2x2Mode={is2x2Mode}
                renderQuickPeekShield={renderQuickPeekShield}
              />
            );
          } else {
            row = r as ParticipantGameMovementRow;

            return (
              <ParticipantsRoundMovementRow
                key={row.boardNo + row.player1.userId + row.player2.userId}
                row={row}
                hasTableNumbers
                isChild={false}
                onGameClick={onGameClick}
                renderQuickPeekShield={renderQuickPeekShield}
              />
            );
          }
        })}

        {total && <TotalMovementRow total={total} />}
      </Table>
    </HorizontalScroll>
  );
}
